// Copyright (c) StrangeBee 2024

import { useHistory, useLocation, useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { CaseOutput, PermissionsEnum } from 'thehive-sdk';
import { THLayout } from '@Molecules/THLayout';
import { ICONS } from '@Constants/iconsApp';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Case/selector';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { THTabs } from '@Molecules/Inputs/THTabs/THTabs';
import { AttachmentListPanelContent } from './AttachmentListPanelContent';
import { useContext } from 'react';
import { StatusContext } from '@Context/StatusProvider';

import './CaseAttachmentList.scss';

const checkLocation = (location: string, caseId: string): string =>
    ['cases', 'tasks', 'reports'].reduce((acc, sectionId) => {
        const path =
            sectionId === 'cases'
                ? RouteBuilder.compute('cases.attachments', { caseId })
                : RouteBuilder.compute('cases.attachments-section', { caseId, sectionId });
        return location.includes(path) ? path : acc;
    });

const CaseAttachmentListPage = () => {
    const i = useLocalMessage();
    const history = useHistory();
    const location = useLocation();
    const { licenseCapabilities } = useContext(StatusContext);

    const { caseId } = useParams<{ caseId: string; sectionId: 'cases' | 'tasks' | 'reports' }>();
    const { data: caseData } = useOneQuery<CaseOutput>({
        controller: client.CaseController,
        selector: computeGetSelector({ caseId }),
    });
    const { userPermissions = [] } = caseData || {};
    const hasPermissions = useSplitPermissions(userPermissions, [PermissionsEnum.MANAGE_CASE_UPDATE]);

    return (
        <THLayout>
            <THLayout.Content>
                {/* Tab Navigation */}
                <THTabs
                    defaultActiveKey={checkLocation(location.pathname, caseId)}
                    type="card"
                    size="small"
                    onTabClick={(key) => history.push(key)}
                >
                    <THTabs.TabPane
                        tab={i('case.attachment.tab.cases')}
                        key={RouteBuilder.compute('cases.attachments-section', {
                            caseId,
                            sectionId: 'cases',
                        })}
                    />
                    <THTabs.TabPane
                        tab={i('case.attachment.tab.tasks')}
                        key={RouteBuilder.compute('cases.attachments-section', {
                            caseId,
                            sectionId: 'tasks',
                        })}
                    />
                    {licenseCapabilities?.case?.reporting && (
                        <THTabs.TabPane
                            tab={i('case.attachment.tab.reports')}
                            key={RouteBuilder.compute('cases.attachments-section', {
                                caseId,
                                sectionId: 'reports',
                            })}
                        />
                    )}
                </THTabs>
                {/* Content of current panel */}
                <AttachmentListPanelContent hasPermission={hasPermissions.updateCase} />
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.attachments',
        icon: <ICONS.FileSearchOutlined />,
    },
    path: RouteBuilder.compute('cases.attachments'),
    content: <CaseAttachmentListPage />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.attachments' },
};

export const RouteAttachment: RouteType = {
    path: RouteBuilder.compute('cases.attachments-section'),
    content: <CaseAttachmentListPage />,
    space: ['user'],
    exact: true,
};
